import React, {useEffect, useState} from 'react';
import {
    DEFAULT_CONTENT_DASHBOARD_TAB,
    HTML_CONTENT_TYPE_HEADERS, HTML_CONTENT_TYPE_LIST_ITEMS
} from "../../constants/content.constant";
import {useParams} from "react-router-dom";
import ShowLink from "../../../common/ui/show-link";
import {getSingleBlogPost, updateSingleBlogPost} from "../../services/blog-post-data-service";
import ShowProgressSpinner from "../../../common/ui/show-progress-spinner";
import _ from "lodash";
import AddEditRichText from "../rich-text-editor/add-edit-rich-text";
import RichTextEditor from "../rich-text-editor/rich-text-editor";
import ShowContentLineList from "./show-content-line-list";
import {searchPromptsWithSearchCriteria} from "../../services/prompt-data-service";
import {
    addSingleBlogPostContent,
    searchBlogPostContentsWithSearchCriteria, updateSingleBlogPostContent
} from "../../services/blog-post-content-data-service";
import {SAVE_BLOG_POST_TIME_DELAY} from "../../constants/blog.constant";
import CopyDataToClipboard from "../../../common/ui/copy-data-to-clipboard";
import {
    addTextForContentLine,
    markAsDraftBlogPost,
    markAsReadyBlogPost,
    prepareHtmlContentDetails
} from "../../utils/blog-content-util";

const DesignBlogPostDashboard = () => {
    const {blogPostId} = useParams();
    const [initialStep, setInitialStep] = useState(true);
    const [populateFirstTime, setPopulateFirstTime] = useState(true);
    const [selectedTab, setSelectedTab] = useState(DEFAULT_CONTENT_DASHBOARD_TAB);
    const [blogPost, setBlogPost] = useState(null);
    const [saveInProgress, setSaveInProgress] = useState(false);
    const [blogPostContent, setBlogPostContent] = useState({});
    const [contentLines, setContentLines] = useState(null);
    const [inputTexts, setInputTexts] = useState(null);
    const [htmlContent, setHtmlContent] = useState(null);
    const [promptsList, setPromptsList] = useState(null);
    const [selectedContentLine, setSelectedContentLine] = useState(null);
    const [selectedContentLineIndex, setSelectedContentLineIndex] = useState(null);
    let [changesCount, setChangesCount] = useState(0);
    const [sectionTitlesMap, setSectionTitlesMap] = useState({});

    useEffect(() => {
        if (initialStep) {
            getBlogPostById(blogPostId);
            getBlogPostContentByPostId(blogPostId);
            getPromptList();
            setInitialStep(false);
        }
    }, [initialStep, blogPostId]);

    useEffect(() => {
        if (!_.isEmpty(contentLines)) {
            prepareHtmlContent();
        }
    }, [contentLines])

    const getBlogPostById = async (blogPostId) => {
        const blogPostResult = await getSingleBlogPost(blogPostId);
        const existingBlogPost = blogPostResult.data();
        existingBlogPost.id = blogPostResult.id;
        setBlogPost(existingBlogPost);
    }

    const getBlogPostContentByPostId = async (blogPostId) => {
        const searchCriteriaList = [{field: 'blogPostId', condition: '==', value: blogPostId}];
        const blogPostContentListResult = await searchBlogPostContentsWithSearchCriteria(searchCriteriaList);
        let existingBlogPostContent: any = {};

        if (blogPostContentListResult.length === 0) {
            existingBlogPostContent = {
                blogPostId: blogPostId,
            }

            const newBlogPostContentResult = await addSingleBlogPostContent(existingBlogPostContent);
            existingBlogPostContent.id = newBlogPostContentResult.id;
            setBlogPostContent(existingBlogPostContent);
        } else {
            existingBlogPostContent = blogPostContentListResult[0]
        }

        setBlogPostContent(existingBlogPostContent);
        setInputTexts(_.compact(existingBlogPostContent.inputTexts || []));
        setContentLines(existingBlogPostContent.contentLines);
        setHtmlContent(existingBlogPostContent.htmlContent);
    }

    const saveBlogPostContent = async () => {
        setSaveInProgress(true);
        const existingBlogPostContent: any = {...blogPostContent};
        existingBlogPostContent.contentLines = [...contentLines];
        existingBlogPostContent.inputTexts = [...inputTexts];
        existingBlogPostContent.htmlContent = htmlContent;

        await updateSingleBlogPostContent(existingBlogPostContent);
        setSaveInProgress(false);
    }

    const getPromptList = async () => {
        const searchCriteriaList = [
            {field: 'type', condition: '==', value: 'Blog'},
            {field: 'status', condition: '==', value: 'Enabled'}
        ];

        const promptListResult = await searchPromptsWithSearchCriteria(searchCriteriaList);
        setPromptsList(promptListResult);
    }

    const incrementChangesCount = () => {
        setChangesCount(++changesCount);
    }

    const resetChangesCount = () => {
        setChangesCount(0);
    }

    useEffect(() => {
        const timeoutId =
            setTimeout(async () => {
                if (changesCount > 0) {
                    resetChangesCount();
                    saveBlogPostContent();
                }
            }, SAVE_BLOG_POST_TIME_DELAY);

        return () => clearTimeout(timeoutId);
    }, [changesCount]);

    const prepareHtmlContent = () => {
        const existingContentLines = [...contentLines];
        const existingSectionTitlesMap = {...sectionTitlesMap} || {};

        const htmlContentText = prepareHtmlContentDetails(existingContentLines, existingSectionTitlesMap, populateFirstTime);
        setHtmlContent(htmlContentText);
        setSectionTitlesMap(existingSectionTitlesMap);

        if (populateFirstTime) {
            setPopulateFirstTime(false);
        }

        incrementChangesCount();
    }

    const openAddTextForContentLine = (contentLine, contentLineIndex) => {
        setSelectedContentLine(contentLine);
        setSelectedContentLineIndex(contentLineIndex);
    }

    const handleAddTextForContentLine = (sectionContentLines) => {
        const existingContentLines = [...contentLines];
        const newContentLines = addTextForContentLine(existingContentLines, sectionContentLines, selectedContentLineIndex);

        setContentLines(newContentLines);
        setSelectedContentLine(null);
        setSelectedContentLineIndex(null);
    }

    const handleMarkAsDraftBlogPost = async () => {
        setSaveInProgress(true);
        const existingBlogPost = {...blogPost};

        await markAsDraftBlogPost(existingBlogPost);

        setBlogPost(existingBlogPost);
        setSaveInProgress(false);
    }

    const handleMarkAsReadyBlogPost = async () => {
        setSaveInProgress(true);
        const existingBlogPost = {...blogPost};

        await markAsReadyBlogPost(existingBlogPost);

        setBlogPost(existingBlogPost);
        setSaveInProgress(false);
    }

    return (
        <>

            <div className="page-container">

                <div className="items-center justify-start p-4 w-full">

                    <div className="flex items-center justify-between">
                        <div className={'flex items-center justify-start'}>
                            <ShowLink url="/content" label='Back'/>
                            <h3 className="text-xl ml-2">
                                Design Blog Post Content
                            </h3>
                        </div>

                        <h3 className="ml-4 text-xl font-bold text-purple-500">
                            {blogPost?.title}
                            <span className="button-purple-outline px-2 ml-2">
                                <span className={'text-lg font-semibold'}>{blogPost?.status}</span>
                            </span>
                        </h3>

                        <div className="-mt-1">
                            <ShowProgressSpinner dataFetchInProgress={saveInProgress}/>
                        </div>

                        <div className={'mt-2 flex items-center justify-start'}>
                            {/*<div>
                                <CopyDataToClipboard value={htmlContent}/>
                            </div>*/}

                            {blogPost && blogPostContent &&
                                <AddEditRichText
                                    title={blogPost?.title}
                                    promptsList={promptsList}
                                    contentLines={contentLines}
                                    setContentLines={setContentLines}
                                    selectedContentLine={selectedContentLine}
                                    setSelectedContentLine={setSelectedContentLine}
                                    inputTexts={inputTexts}
                                    setInputTexts={setInputTexts}
                                    handleAddTextForContentLine={handleAddTextForContentLine}
                                    prepareHtmlContent={prepareHtmlContent}/>
                            }

                            <button
                                className="button-outline-green px-3 py-2 ml-2 mr-1 my-1"
                                onClick={() => handleMarkAsDraftBlogPost()}>
                                Mark as Draft
                            </button>

                            <button
                                className="button-outline-green px-3 py-2 m-1"
                                onClick={() => handleMarkAsReadyBlogPost()}>
                                Mark as Ready
                            </button>

                            <button
                                className="button-green px-3 py-2 m-1"
                                onClick={saveBlogPostContent}>
                                Save Blog Post Content
                            </button>
                        </div>
                    </div>

                    <div className="justify-center">

                        <div className="flex justify-start">
                            <div className="m-4 w-3/5">
                                {contentLines &&
                                    <ShowContentLineList
                                        openAddTextForContentLine={openAddTextForContentLine}
                                        prepareHtmlContent={prepareHtmlContent}
                                        setContentLines={setContentLines}
                                        contentLines={contentLines}
                                        promptsList={promptsList}
                                        sectionTitlesMap={sectionTitlesMap}
                                        setSectionTitlesMap={setSectionTitlesMap}
                                    />
                                }
                            </div>

                            {htmlContent &&
                                <div
                                    className="w-2/5 border border-gray-400 rounded-lg bg-gray-400 m-4 p-4 html-content">
                                    <RichTextEditor
                                        readOnly={true}
                                        existingContent={htmlContent}
                                        setContentLines={null}/>
                                </div>
                            }
                        </div>

                    </div>

                </div>

            </div>

        </>
    )
}

export default DesignBlogPostDashboard
